import { Injectable } from '@angular/core';
import { catchError, concatMap, defaultIfEmpty, Observable, of, tap } from 'rxjs';
import { OrderRoutes } from '../../features/orders/orders-routes.enum';
import { Message } from '../enums/message.enum';
import { OrderFlowType, PRODUCT_TYPES, ProductStatusType, ProductTracker, StepStatusType } from '../models/order/order.model';
import { InternetFlow } from '../models/order/workflow/internet/internet-flow.model';
import { ServiceInfoCollectionData } from '../models/order/workflow/service-info-collection.model';
import { WirelessFlowType, WirelessSetupData } from '../models/order/workflow/wireless/wireless-flow.model';

import { Session, Token } from '../models/Gateway';
import { UserDetails } from '../models/Login';
import { LicResponse } from '../models/order/lic/lic.model';
import { ServiceAvailabilityResponse } from '../models/order/api-models/order-sq-response.model';
import { TokenUtil } from '../util/token.util';
import { LicBaseService } from './lic-base.service';

export interface LaunchContext {
    Token: Token;
    Session: Session;
    UserId: string;
}

// Redirect error that also tells the agent why the launch was sent back to that step.
export class LicLaunchRedirectError {
    constructor(public readonly redirect: string, public readonly message: string) { }
}

@Injectable({
    providedIn: 'root'
})
export class LicApiCallService extends LicBaseService {

    processApiCalls(licResponse: LicResponse) {
        const sqState = this.serviceInformationCollectionStateService.state() as ServiceInfoCollectionData;
        const internetPdState = this.internetPackageSelectionStateService.internetState() as InternetFlow;
        const wirelessPdState = this.wirelessSetupStateService.state() as WirelessSetupData;
        const orderNumber = licResponse?.ApiResponses?.PreOrder?.OrderNumber;
        let observable: Observable<unknown> = of(null);
        const sqRequested = sqState.status === StepStatusType.Completed;
        if (sqRequested) {
            observable = observable.pipe(
                concatMap(() => this.processSQ()),
                concatMap(response => this.callCache(orderNumber).pipe(
                    tap(() => this.saveServiceStatuses()),
                    tap(() => this.ensureSQProductsSucceeded(response, licResponse))
                ))
            );
        }

        const internetPlanCode = internetPdState?.internetPackageSelection?.selectedPlanCode;
        if (internetPlanCode) {
            observable = observable.pipe(
                tap(() => sqRequested && this.ensureLaunchedInternetOffered(internetPlanCode)),
                concatMap(() => this.processInternetPD()),
                tap(() => this.orderStateServiceV2.updateOrderFlowType(OrderFlowType.Internet))
            )
        }

        if (wirelessPdState?.selectedCustomerCode === WirelessFlowType.NEW) {
            observable = observable.pipe(
                concatMap(() => this.processWirelessPD()),
                tap(() => this.orderStateServiceV2.updateOrderFlowType(OrderFlowType.Wireless))
            );
        }

        if (wirelessPdState?.selectedCustomerCode === WirelessFlowType.EXISTING) {
            observable = observable.pipe(
                concatMap(() => this.processExistingWirelessPD())
            );
        }
        return observable.pipe(defaultIfEmpty(null));
    }

    processSQ() {
        const { customerInformation, serviceAvailability, billingAddress } = this.serviceInformationCollectionStateService.state() as ServiceInfoCollectionData
        return this.orderService.getAvailableServices_SQ(customerInformation, serviceAvailability, billingAddress).pipe(
            catchError((error) => {
                console.error('Error occurred while fetching available services:', error);
                throw { redirect: OrderRoutes.SERVICE_INFORMATION };
            }),
            tap((resp) => {
                this.availableServices.set(resp);
                const error = this.getErrorResponseSQ(resp);
                if (error) {
                    console.error('Error in service availability response:', error);
                    throw { redirect: OrderRoutes.SERVICE_INFORMATION };
                }
            }));
    }

    // The launched plan must be one of SQ's Internet offers; otherwise PD and CC answer success
    // with an empty component and no credit decision, and Credit Check cannot route.
    // The service address decides availability, so the agent is sent back to Service Information.
    ensureLaunchedInternetOffered(planCode: string) {
        const launchedCode = planCode.trim().toLowerCase();
        const offered = this.internetData()?.some(component => component?.Code?.trim().toLowerCase() === launchedCode);
        if (!offered) {
            console.error('Launched internet plan is not offered by service availability:', planCode);
            throw new LicLaunchRedirectError(OrderRoutes.SERVICE_INFORMATION, Message.LaunchInContextInternetUnavailableMessage);
        }
    }

    processInternetPD() {
        return this.orderService.getInternetProductDetails().pipe(
            catchError((error) => {
                console.error('Error occurred while fetching internet product details:', error);
                throw { redirect: OrderRoutes.INTERNET_PACKAGE_SELECTION };
            }),
            concatMap(resp => this.callCacheForPDResponse(resp)),
            tap((resp) => {
                const error = this.getErrorResponse(resp, PRODUCT_TYPES.INTERNET);
                if (error) {
                    console.error('Error in internet product details response:', error);
                    throw { redirect: OrderRoutes.INTERNET_PACKAGE_SELECTION };
                }
            })
        );
    }

    processWirelessPD() {
        return this.orderService.getWirelessProductDetails().pipe(catchError((error) => {
            console.error('Error occurred while fetching wireless product details:', error);
            throw { redirect: OrderRoutes.WIRELESS_SETUP };
        }),
            concatMap(resp => this.callCacheForPDResponse(resp)),
            tap((resp) => {
                const error = this.getErrorResponse(resp, PRODUCT_TYPES.WIRELESS);
                if (error) {
                    console.error('Error in wireless product details response:', error);
                    throw { redirect: OrderRoutes.WIRELESS_SETUP };
                }
            })
        );
    }

    processExistingWirelessPD() {
         return this.orderService.getWirelessProductDetails().pipe(catchError((error) => {
            console.error('Error occurred while fetching wireless product details:', error);
            throw { redirect: OrderRoutes.WIRELESS_SETUP };
        }),
            concatMap(resp => this.callCacheForPDResponse(resp)),
            tap(() => {
                throw { redirect: OrderRoutes.WIRELESS_SETUP };
            })
        );
    }

    private callCacheForPDResponse<T>(response: T): Observable<T> {
        // Conditional responses must be available to the destination screen before redirecting.
        const orderNumber = this.orderStateService.getPreOrder()?.OrderNumber;
        return this.callCache(orderNumber).pipe(concatMap(() => of(response)));
    }

    getErrorResponse(resp, productType: string): any {
        const products = resp?.ResponseHeader?.ProductResponse;
        if (!Array.isArray(products)) return null;

        const errors = products.filter(p => p?.Type?.trim().toLowerCase() === productType.toLowerCase()
            && String(p?.Code ?? '').trim() !== '0000');

        return errors[0]
            || null;
    }

    getErrorResponseSQ(resp: any) {
        const topCode = resp?.Response?.Code;
        return topCode && topCode !== '0000' ? topCode : null;
    }

    ensureSQProductsSucceeded(response: ServiceAvailabilityResponse, licResponse: LicResponse) {
        const requestedProducts: string[] = [];
        if (licResponse?.ApiResponses?.InternetFlow) requestedProducts.push(PRODUCT_TYPES.INTERNET.toLowerCase());
        if (licResponse?.ApiResponses?.WirelessFlow) requestedProducts.push(PRODUCT_TYPES.WIRELESS.toLowerCase());
        const productError = response?.Response?.ProductResponse?.find(
            product => requestedProducts.includes(product?.Type?.trim().toLowerCase())
                && String(product?.Code ?? '').trim() !== '0000'
        );
        if (productError) {
            throw { redirect: OrderRoutes.BUNDLE_HOMEPAGE };
        }
    }

    processLaunchContext(launchId: string) {
        const endpoint = `${this.processLaunchContextUrl}${launchId}`;
        return this.dataService.get<LaunchContext>(endpoint, null, true, 'json').pipe(
            tap((launchContext) => {
                let token = launchContext?.Token ?? null;
                if (token?.issued_at && token?.expires_in) {
                    token = TokenUtil.fillToken(token);
                }
                this.appStateService.setSession(launchContext?.Session ?? null);
                this.appStateService.setToken(token);
                this.userStateService.setUser({ UserId: launchContext?.UserId } as unknown as UserDetails);
            })
        );
    }

    callCache(currentOrderNumber: string) {
        return this.orderApiResponseStateService.loadOrderData(currentOrderNumber).pipe(
            tap(() => {
                this.orderStateApiBridgeService.init();
            }));
    }

    saveServiceStatuses() {
        const productResponse = this.orderApiResponseStateService.serviceAvailabilityProductResponse() || []
        const serviceStatuses: ProductTracker[] = []

        // Check Video services (DTV or ATV); AT&T SMB never offers Video
        if (this.isVideoAvailable() && !this.isSMB()) {
            let status: ProductStatusType = ProductStatusType.AVAILABLE_NOT_STARTED
            if (this.videoProductBlocked() && !this.videoData()?.length) {
                status = ProductStatusType.NOT_AVAILABLE
            } else if (this.videoProductBlocked() && this.videoData()?.length) {
                status = ProductStatusType.REQUIRES_ATTENTION
            }

            serviceStatuses.push({
                key: PRODUCT_TYPES.VIDEO,
                status,
                available: true,
                priority: 1
            })
        }

        // Check Internet services
        if (this.isInternetAvailable()) {

            let status: ProductStatusType = ProductStatusType.AVAILABLE_NOT_STARTED
            if (this.internetProductBlocked() && !this.internetData().length) {
                status = ProductStatusType.NOT_AVAILABLE
            } else if (this.internetProductBlocked() && this.internetData().length) {
                status = ProductStatusType.REQUIRES_ATTENTION
            }

            serviceStatuses.push({
                key: PRODUCT_TYPES.INTERNET,
                status,
                available: true,
                priority: 2
            })
        }

        // Check Wireless services
        const wirelessAvailable = productResponse.some((item: any) =>
            item.Type === PRODUCT_TYPES.WIRELESS && item?.Code !== '2005'
        )
        if (wirelessAvailable) {
            serviceStatuses.push({
                key: PRODUCT_TYPES.WIRELESS,
                status: ProductStatusType.AVAILABLE_NOT_STARTED,
                available: true,
                priority: 3
            })
        }

        // Check Voice services
        if (this.isVoiceAvailable()) {
            serviceStatuses.push({
                key: PRODUCT_TYPES.VOICE,
                status: ProductStatusType.AVAILABLE_NOT_STARTED,
                available: true,
                priority: 4
            })
        }

        // Check Protection services
        const protectionAvailable = productResponse.some((item: any) =>
            item.Type === PRODUCT_TYPES.PROTECTION && item?.Code === '0000'
        )
        if (protectionAvailable) {
            serviceStatuses.push({
                key: PRODUCT_TYPES.PROTECTION,
                status: ProductStatusType.AVAILABLE_NOT_STARTED,
                available: true,
                priority: 5
            })
        }

        this.orderStateServiceV2.updateOrder({
            serviceStatuses: serviceStatuses
        })
    }



}
